// Запросы push-уведомлений: /api/push/*.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type PushDevice = {
  id: string
  device_label: string | null
  service: string
  created_at: string
  last_ok_at: string | null
  failed_count: number
}

export type KindPref = {
  kind: string
  label: string
  description: string
  url: string
  scheduled: boolean
  enabled: boolean
  time: string | null // «08:00:00»
}

export type PushPrefs = {
  kinds: KindPref[]
  advice_time: string
  weekly_report_dow: number
  weekly_report_time: string
}

export type SubscriptionBody = {
  endpoint: string
  keys: { p256dh: string; auth: string }
  device_label: string
}

export type PrefsChange = Record<string, { enabled?: boolean; time?: string }>

export const pushKeys = {
  all: ['push'] as const,
  devices: ['push', 'devices'] as const,
  prefs: ['push', 'prefs'] as const,
}

export const pushApi = {
  vapidKey: () => api.get<{ public_key: string }>('/push/vapid-key'),
  devices: () => api.get<PushDevice[]>('/push/subscriptions'),
  subscribe: (body: SubscriptionBody) => api.post<PushDevice>('/push/subscriptions', body),
  unsubscribe: (endpoint: string) => api.post<void>('/push/unsubscribe', { endpoint }),
  remove: (id: string) => api.delete(`/push/subscriptions/${id}`),
  test: () => api.post<{ sent: number }>('/push/test', {}),
  prefs: () => api.get<PushPrefs>('/push/prefs'),
  savePrefs: (kinds: PrefsChange) => api.put<PushPrefs>('/push/prefs', { kinds }),
}

export function usePushDevices() {
  return useQuery({ queryKey: pushKeys.devices, queryFn: pushApi.devices })
}

export function usePushPrefs() {
  return useQuery({ queryKey: pushKeys.prefs, queryFn: pushApi.prefs })
}

export function useSavePrefs() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: pushApi.savePrefs,
    onSuccess: (data) => client.setQueryData(pushKeys.prefs, data),
  })
}

export function useRemoveDevice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: pushApi.remove,
    onSettled: () => client.invalidateQueries({ queryKey: pushKeys.devices }),
  })
}

export function useTestPush() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: pushApi.test,
    onSettled: () => client.invalidateQueries({ queryKey: pushKeys.devices }),
  })
}
